import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'components/ui';
import ProductList from 'components/Content/ProductList';
import contentConstants from 'constants/content';
import localeUtils from 'utils/LanguageLocale';
import SavedForLaterCarouselEN from 'components/SaveForLater/SavedForLaterCarousel/locales/SavedForLaterCarousel_en_US';
import SavedForLaterCarouselFR from 'components/SaveForLater/SavedForLaterCarousel/locales/SavedForLaterCarousel_fr_CA';
import Empty from 'constants/empty';
import Box from 'components/Box';
import {
    colors, space, buttons, fontSizes, fontWeights
} from 'style/config';
import Button from 'components/Button';
import basketConstants from 'constants/Basket';
import OutOfStockButton from 'components/AddToBasketButton/OutOfStockButton/OutOfStockButton';
import skuHelpers from 'utils/skuHelpers';
import { withSavedForLaterCarouselProps } from 'viewModel/saveForLater/withSavedForLaterCarouselProps';

const { PRODUCT_LIST_GROUPING } = contentConstants;
const { getLocalisedDataFromResource } = localeUtils;

function SavedForLaterCarousel(props) {
    const {
        saveForLaterCarouselData,
        saveForLaterLoading,
        removeSavedForLater,
        showSavedForLaterModal,
        showChangeMethodModal,
        moveItemToBasket,
        userId
    } = props;

    const getText = getLocalisedDataFromResource(SavedForLaterCarouselEN, SavedForLaterCarouselFR);

    const hasData = saveForLaterCarouselData?.skuList?.length > 0;

    const handleViewAll = event => {
        event.preventDefault();
        showSavedForLaterModal({ isOpen: true });
    };

    if (!saveForLaterLoading && !hasData) {
        return null;
    }

    const handleRemoveClick = sku => event => {
        event.preventDefault();
        event.stopPropagation();
        removeSavedForLater({
            sku,
            userId,
            listId: saveForLaterCarouselData?.listId
        });
    };

    const renderRemoveLink = ({ sku }) => {
        return (
            <Link
                onClick={handleRemoveClick(sku)}
                color='blue'
            >
                {getText('remove')}
            </Link>
        );
    };

    const renderMoveToBasketButton = ({ sku, analyticsContext }) => {
        const { BasketType } = basketConstants;
        const isOutOfStock = skuHelpers.isSkuOutOfStockByBasketType(sku);

        if (isOutOfStock) {
            return (
                <OutOfStockButton
                    basketType={BasketType.Standard}
                    variant='secondary'
                    size='sm'
                    analyticsContext={analyticsContext}
                    sku={sku}
                    customStyle={styles.button}
                />
            );
        }

        const handleClick = event => {
            event.preventDefault();
            event.stopPropagation();

            showChangeMethodModal({
                isOpen: true,
                item: { sku, qty: 1 },
                itemDeliveryMethod: null,
                onChangeMethod: handleChangeMethod
            });
        };

        return (
            <Button
                variant='secondary'
                size='sm'
                onClick={handleClick}
                css={styles.button}
            >
                {getText('moveToBasket')}
            </Button>
        );
    };

    // Callback when user confirms delivery method
    const handleChangeMethod = params => {
        const { skuId, productId, qty = 1, deliveryOption } = params;

        return moveItemToBasket({
            skuId,
            productId,
            qty,
            deliveryOption,
            userId,
            listId: saveForLaterCarouselData?.listId
        });
    };

    return (
        <Box css={styles.container}>
            {!saveForLaterLoading && hasData && (
                <Box css={styles.header}>
                    <Box css={styles.title}>{`${getText('savedForLaterTitle')} (${saveForLaterCarouselData?.totalItems || 0})`}</Box>
                    <Link
                        onClick={handleViewAll}
                        color='blue'
                        css={styles.viewAllLink}
                    >
                        {getText('viewAll')}
                    </Link>
                </Box>
            )}

            <ProductList
                key='saved-for-later-carousel'
                skuList={saveForLaterCarouselData?.skuList || Empty.Array}
                title={saveForLaterLoading ? `${getText('savedForLaterTitle')} (${saveForLaterCarouselData?.totalItems || 0})` : null}
                grouping={[PRODUCT_LIST_GROUPING.SHOW_PRICE, PRODUCT_LIST_GROUPING.SHOW_MARKETING_FLAGS, PRODUCT_LIST_GROUPING.SHOW_ADD_BUTTON]}
                podId={'saved-for-later'}
                totalResults={saveForLaterCarouselData?.skuList?.length || 0}
                isCarousel={true}
                variant={'Horizontal Carousel'}
                renderRightAction={saveForLaterLoading ? undefined : renderRemoveLink}
                showSkeleton={saveForLaterLoading}
                renderBiButton={saveForLaterLoading ? undefined : renderMoveToBasketButton}
                addButtonText={saveForLaterLoading ? undefined : getText('moveToBasket')}
                marginTop={0}
                marginBottom={0}
            />
        </Box>
    );
}

const styles = {
    container: {
        backgroundColor: colors.nearWhite,
        padding: `${space[4]}px ${space.container}px ${space[5]}px`,
        margin: `${space[6]}px ${space['-container']}px 0`,
        '& ul::before, & ul::after': {
            backgroundColor: colors.nearWhite
        }
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: `${space[4]}px`
    },
    title: {
        fontSize: `${fontSizes['lg-bg']}px`,
        fontWeight: fontWeights.bold
    },
    viewAllLink: {
        textDecoration: 'none',
        cursor: 'pointer'
    },
    button: {
        marginTop: `${space[4]}px`,
        width: '120px',
        height: `${[buttons.HEIGHT]}px`
    }
};

SavedForLaterCarousel.propTypes = {
    saveForLaterCarouselData: PropTypes.shape({
        skuList: PropTypes.array,
        totalItems: PropTypes.number,
        listId: PropTypes.string
    }),
    saveForLaterLoading: PropTypes.bool,
    removeSavedForLater: PropTypes.func.isRequired,
    showSavedForLaterModal: PropTypes.func.isRequired,
    showChangeMethodModal: PropTypes.func.isRequired,
    moveItemToBasket: PropTypes.func.isRequired,
    userId: PropTypes.string
};

export default withSavedForLaterCarouselProps(SavedForLaterCarousel);
